import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { HelpCircle } from "lucide-react";
import { Card } from "@/components/Card/Card";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MonthlySalesTrendChart as MonthlySalesTrendChartType } from "../../api/fetchROIDashboardData/types";
import { formatCurrency } from "@/modules/stochastic/features/DashboardPage/utils/chart";

interface MonthlySalesTrendChartProps {
  data: MonthlySalesTrendChartType | null;
}

const formatYAxis = (value: number): string => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value}`;
};

export const MonthlySalesTrendChart: React.FC<MonthlySalesTrendChartProps> = ({
  data,
}) => {
  if (!data) {
    return null;
  }

  return (
    <Card className="w-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Monthly Sales Trend
          </h3>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Comparison of total residential sales vs matchback sales
                  (customers acquired through direct mail) for {data.year}.
                </p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>

        <div className="h-80">
          <ResponsiveContainer height="100%" width="100%">
            <LineChart
              data={data.data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="month"
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickFormatter={formatYAxis}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value: number) => [formatCurrency(value)]}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              />
              <Legend
                formatter={(value) => (
                  <span className="text-sm text-gray-600">{value}</span>
                )}
                iconType="rect"
                wrapperStyle={{ paddingTop: 20 }}
              />
              <Line
                dataKey="residentialSales"
                dot={false}
                name="Total Sales"
                stroke="#3B82F6"
                strokeWidth={2}
                type="monotone"
              />
              <Line
                dataKey="matchbackSales"
                dot={false}
                name="Matchback Sales"
                stroke="#10B981"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};
